import { apiGetProjectUsers, apiGetProjectInvitations } from "~/providers/api";
import { IClientProjectInvitation } from "~/providers/client-interfaces";
import { IUser, IUserAdmin, TUserKeyRole } from "~/providers/interfaces";
import { formatDate } from '~/utils/utils';
import { useNonHookTrans } from '~/hooks/useTrans';

export interface IUserItem {
	email: string;
	state: string;
	createdAt: string;
	role: string;
	data: IUser;
	notification: boolean;
}

export interface IInvitedUserItem extends Pick<IUserItem, "email" | "state" | "createdAt" | "role"> {
	data: IClientProjectInvitation;
}

function sortByOwner(inputArray: Array<IUserItem>): Array<IUserItem> {
	const sortingArr: Array<TUserKeyRole> = [
		"owner",
		"admin",
		"visitor",
	];

	inputArray.sort((aItem, bItem) => sortingArr.indexOf(aItem.data.role) - sortingArr.indexOf(bItem.data.role));

	return inputArray;
}

interface IGetClientUsersDataOutput {
	ownerEmail: string;
	users: Array<IUserItem>;
	invitedUsers: Array<IInvitedUserItem>;
	ownershipUsers: Array<string>;
	errors: Array<string>;
	noErrors: boolean;
}

export function userToItem(user: IUser | IUserAdmin, isCzech?: boolean): IUserItem {
	const { trans, transUserRole } = useNonHookTrans();

	return {
		email: user.email,
		state: trans("userList.state.active"),
		createdAt: formatDate(new Date(user.createdAt), isCzech),
		role: transUserRole(user.role),
		notification: user.emailNotifications,
		data: user,
	};
}

export async function getClientUsersData(isInternal: boolean, projectId: number, userEmail: string, lang: string, isCzech: boolean) {
	const [projectUsers, invitationsData] = await Promise.all([
		apiGetProjectUsers(isInternal ? "admin" : "client", projectId, lang),
		apiGetProjectInvitations(isInternal ? "admin" : "client", projectId, lang),
	]);
	const output: IGetClientUsersDataOutput = {
		ownerEmail: "",
		users: [],
		invitedUsers: [],
		ownershipUsers: [],
		errors: [],
		noErrors: false,
	};
	const { transInvitationState, transUserRole } = useNonHookTrans();

	if (projectUsers.noErrors) {
		output.users = sortByOwner(projectUsers.data.map(user => userToItem(user, isCzech)));

		const filtered = projectUsers.data.filter(item => item.role === "owner");

		if (filtered.length === 1) {
			output.ownerEmail = filtered[0].email;
		}

		// naplnime seznam uzivatelu pro ownership
		if (isInternal) {
			output.ownershipUsers = output.users.filter(user => user.data.role !== "owner").map(user => user.email);
		} else {
			output.users.forEach(userItem => {
				if (userItem.email !== userEmail && userItem.data.role !== "owner") {
					output.ownershipUsers.push(userItem.email);
				}
			});
		}
	}

	if (invitationsData.noErrors) {
		invitationsData.data.forEach(invitation => {
			output.invitedUsers.push({
				email: invitation.invitedEmail,
				state: transInvitationState(invitation),
				createdAt: formatDate(new Date(invitation.createdAt), isCzech),
				role: transUserRole(invitation.invitedRole),
				data: invitation,
			});
		});

		output.invitedUsers.sort((aItem, bItem) => aItem.email.localeCompare(bItem.email));
	}

	output.errors = [
		...output.errors,
		...invitationsData.errors,
	];
	output.noErrors = output.errors.length === 0;

	return output;
}
